import type { AiImageAnnotation } from "../schema"

export interface AnnotationBadge {
	cx: number
	cy: number
	r: number
	text: string
	fontSize: number
}

export interface BoxGeometry {
	kind: "box"
	x: number
	y: number
	width: number
	height: number
	radius: number
	strokeWidth: number
	badge?: AnnotationBadge
}

export type AnnotationGeometry = BoxGeometry

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value))

const clamp01 = (value: number) =>
	Number.isFinite(value) ? clamp(value, 0, 1) : 0

// Sizes key off the shorter side so a mark stays proportional on a wide screenshot.
const scaleOf = (width: number, height: number) => Math.min(width, height)

export const strokeWidthFor = (width: number, height: number) =>
	clamp(scaleOf(width, height) * 0.006, 2.5, 9)

const round = (value: number) => Math.round(value * 10) / 10

// Every mark is a rounded box around the region the vision pass measured. The
// box IS the target, so it cannot point at the wrong place the way a drawn
// arrow could.
export function buildAnnotationGeometry(
	annotation: AiImageAnnotation,
	width: number,
	height: number,
): BoxGeometry {
	const scale = scaleOf(width, height)
	const strokeWidth = strokeWidthFor(width, height)
	const pad = clamp(scale * 0.012, 5, 18)

	const fx = clamp01(annotation.x)
	const fy = clamp01(annotation.y)
	const fw = clamp(Number.isFinite(annotation.w) ? annotation.w : 0, 0, 1 - fx)
	const fh = clamp(Number.isFinite(annotation.h) ? annotation.h : 0, 0, 1 - fy)

	const minSide = scale * 0.02
	const rawLeft = fx * width - pad
	const rawTop = fy * height - pad
	const rawWidth = Math.max(fw * width, minSide) + pad * 2
	const rawHeight = Math.max(fh * height, minSide) + pad * 2

	// Half the stroke paints outside the path, so the inset keeps the halo on
	// the canvas even for a region flush with the image edge.
	const inset = strokeWidth * 1.6
	const left = clamp(rawLeft, inset, width - inset)
	const top = clamp(rawTop, inset, height - inset)
	const box: BoxGeometry = {
		kind: "box",
		x: round(left),
		y: round(top),
		width: round(clamp(rawWidth, minSide, width - inset - left)),
		height: round(clamp(rawHeight, minSide, height - inset - top)),
		radius: round(clamp(scale * 0.012, 6, 14)),
		strokeWidth,
	}

	if (annotation.type === "arrow-step" && annotation.step !== undefined) {
		const r = clamp(scale * 0.028, 13, 28)
		// The badge sits on the box's top-left corner, pulled inside the canvas
		// when the box hugs the image edge.
		box.badge = {
			cx: round(clamp(box.x, r + strokeWidth, width - r - strokeWidth)),
			cy: round(clamp(box.y, r + strokeWidth, height - r - strokeWidth)),
			r: round(r),
			text: String(annotation.step),
			fontSize: round(r * 1.15),
		}
	}

	return box
}
